import {useState} from "react";
import Modal from "../../../ui-components/Modal";
import {FormattedMessage} from "react-intl";
import {Discount, DiscountRule1Props, QuotationProductEditDiscountModalProps} from "../types";
import Button from "../../../ui-components/Button";
import {DiscountRule} from "../../types";
import RadioGroup from "../../../ui-components/RadioGroup";
import {formatCurrency} from "../quotation-draft/lib/format";
import {round} from "lodash";
import {computeDiscountedValue} from "../lib/utilities";
import {SimpleDiscountEditor} from "./simple-discount-editor";
import {Discount1Editor} from "./discount-1-editor";
import AuthenticationService from "../../../services/AuthenticationService";
import {computeTotals} from "../quotation-draft/lib/utilities";

const DISCOUNT_TYPES: DiscountRule[] = [
  'no_discount',
  'discount_rule_1',
  'absolute',
];

const DISCOUNT_LABELS = {
  no_discount: 'no_discount',
  absolute: 'net_price_discount',
  absolute_percentage: 'absolute_percentage_discount',
  discount_rule_1: 'discount_rule_1_discount',
}

export const EditProductDiscountModal = ({
  currentTotal,
  discount,
  quantity,
  price,
  minQty,
  onConfirm,
  onReset,
  onExit,
  selectedProducts,
  editable
}: QuotationProductEditDiscountModalProps) => {

  // hooks
  const userScopes = AuthenticationService.getUserScopes();

  // utilities
  const getModalTitle = () => {
    if (!editable) {
      return {
        id: 'view_discount',
        defaultMessage: 'Visualizza sconto'
      }
    }
    if (userScopes.includes('quotation:apply_discount')) {
      return {
        id: 'apply_discount',
        defaultMessage: 'Applica sconto'
      }
    }
    if (userScopes.includes('quotation:apply_marginality')) {
      return {
        id: 'apply_marginality',
        defaultMessage: 'Applica marginalità'
      }
    }
  }
  const getDefaultValueByDiscountRule = (t: DiscountRule) => {
    switch (t) {
      case 'no_discount':
        return undefined;
      case 'absolute':
        return currentTotal;
      case "discount_rule_1":
        return {
          discount_1: 0,
          marginality: 0,
          discount_2: 0,
          discount_3: 0,
          transport_eur_kg: 0,
        } as DiscountRule1Props;
      default:
        throw new Error(`Discount rule ${t} not implemented`)
    }
  }

  // state
  const [localDiscount, setLocalDiscount] = useState(
    !discount || discount.type === "no_discount"
      ? ({
          type: "discount_rule_1",
          value: getDefaultValueByDiscountRule("discount_rule_1"),
        } as Discount)
      : discount
  );
  const showResetBtn = !!onReset

  const updateLocalValue = (v: number | DiscountRule1Props) =>
    setLocalDiscount((prev) => ({
      ...prev,
      value: v
    }));
  const renderEditor = (t: DiscountRule) => {
    switch (t) {
      case 'no_discount':
      case 'absolute':
        return (
          <SimpleDiscountEditor
            title={<FormattedMessage id="net_price" />}
            name="absolute-discount"
            value={localDiscount.value as number}
            onChange={updateLocalValue}
            um="€"
            {...{min: 0, step: ".01"}}
            editable={editable}
          />
        );
      case 'discount_rule_1':
        return (
          <Discount1Editor
            value={localDiscount.value as DiscountRule1Props}
            onChange={updateLocalValue}
            productMus={(selectedProducts
              ?.map((p) => p.product.base_um)
              ?.filter((um) => !!um)
            ?? []) as string[]}
            editable={editable}
          />
        )
      default:
        throw new Error(`Discount rule ${t} not implemented`)
    }
  }
  const isConfirmDisabled = ({type, value}: Discount) => {
    switch (type) {
      case 'absolute': {
        const v = value as number;
        return !v || v <= 0 || v === currentTotal;
      }
      case 'absolute_percentage': {
        const v = value as number;
        return !v || v <= 0 || v > 100 || v === 100;
      }
      case 'discount_rule_1': {
        const d1 = (value as DiscountRule1Props)?.discount_1
        const m = (value as DiscountRule1Props)?.marginality
        const transport = (value as DiscountRule1Props)?.transport_eur_kg
        return (!d1 || d1 <= 0 || d1 > 100) && (!m || m <= 0 || m > 100) && (!transport || transport <= 0)
      }
      default:
        return false;
    }
  }
  const discountedTotal = currentTotal !== undefined
    ? quantity && price ? computeTotals(
        quantity,
        minQty,
        price,
        localDiscount,
        quantity
      ).discounted
      : computeDiscountedValue(
        currentTotal,
        localDiscount
      )
    : null;
  const showDiscountedTotal =
    currentTotal !== undefined
    && !!localDiscount.value
    && round(currentTotal, 2) !== round(discountedTotal!, 2);

  return (
    // @ts-ignore
    <Modal
      onExit={onExit}
      // @ts-ignore
      opened={true}
      large={localDiscount.type === 'discount_rule_1'}
    >
      <h1 className="text-center text-xl font-extrabold mt-3">
        <FormattedMessage
          {...getModalTitle()} />
      </h1>
      {selectedProducts.length > 1 && (
        <div className="mt-6 font-semibold text-blue-400">
          <FormattedMessage
            id="user_selected_products_count"
            defaultMessage="Hai selezionato {count} prodotti (non verranno mostrati gli sconti o le marginalità già applicati)."
            values={{count: selectedProducts.length}}
          />
        </div>
      )}
      <div className="flex flex-col gap-3 pt-3 pb-7">
        {editable && <RadioGroup
          title={null}
          description={null}
          options={
            DISCOUNT_TYPES
              .filter((t) => t !== 'no_discount')
              .map((t) => ({
                name: <FormattedMessage id={DISCOUNT_LABELS[t]} />,
                value: t,
              }))
          }
          onChange={(t: DiscountRule) => {
            setLocalDiscount({
              type: t,
              value: getDefaultValueByDiscountRule(t)
            });
          }}
          currentValue={localDiscount.type}
          horizontal
        />
        }
        {currentTotal !== undefined && <>
          <p className="mt-2 font-semibold">
            <FormattedMessage id="current_price" />
          </p>
          <p className="flex items-center gap-2">
            <span className={showDiscountedTotal ? 'line-through text-gray-400' : ''}>
              {formatCurrency(currentTotal!)}
            </span>
            {showDiscountedTotal && <>
              <span className="font-semibold text-red-500">
                {formatCurrency(discountedTotal!)}
              </span>
            </>}
          </p>
        </>}
        {renderEditor(localDiscount.type)}
      </div>
      <div className="w-full flex flex-col sm:flex-row gap-x-4 gap-y-2">
        {showResetBtn && editable && (
          <div className="sm:flex-1">
            <Button
              full
              styleType="redWhite"
              onClick={() => {
                onReset();
                onExit();
              }}
            >
              <FormattedMessage id="reset" />
            </Button>
          </div>
        )}
        {editable && <div className="sm:flex-1">
          <Button
            full
            onClick={() => {
              onConfirm(localDiscount!);
              onExit();
            }}
            disabled={isConfirmDisabled(localDiscount)}
          >
            <FormattedMessage id="confirm"/>
          </Button>
        </div>}
      </div>
    </Modal>
  )
}